// SKAdNetwork conversion-value rule engine for the JS engine.
//
// Port of core/src/skan.rs. SKAN is iOS-only at the OS level, but the
// evaluation (event + properties → conversion value) is pure logic, and on
// React Native the JS engine is the only engine — so `skanProcessEvent()`
// returned null for every event and no conversion value was ever posted to
// Apple from an RN app. The native bridge in `@layers/react-native/src/skan.ts`
// was wired the whole time; it just had nothing to apply.
//
// Values are MONOTONIC: Apple only honours an increasing value within a
// postback window, so a decision is produced only when the value would
// strictly increase, and the floor advances only once the wrapper confirms
// the native update landed (skan.rs:218-276 — the SKAN analogue of ADR 0001).
import { LayersError } from './types.js';

/** Apple's maximum fine conversion value (6-bit field) — skan.rs:30. */
const MAX_FINE_VALUE = 63;

/** Default `low` band ceiling, inclusive — `DEFAULT_LOW_MAX` in skan.rs. */
const DEFAULT_LOW_MAX = 20;
/** Default `medium` band ceiling, inclusive — `DEFAULT_MEDIUM_MAX` in skan.rs. */
const DEFAULT_MEDIUM_MAX = 42;

/**
 * Where the two cut points between Apple's three coarse conversion values sit
 * on the 0..=63 fine scale — port of `CoarseBands` (skan.rs).
 *
 * SKAN 4.0 postbacks carry a coarse value (`low` | `medium` | `high`) in the
 * privacy tiers where Apple withholds the fine value entirely, so a null coarse
 * value is not "less detail" — it is no conversion signal at all for those
 * installs. Every preset rule ships without an explicit `coarseValue`, so one
 * is derived from the fine value instead.
 *
 * The boundaries are Apple-guidance-sensitive, so they are a remote-config knob
 * (`skan.coarseBands`) rather than a released constant.
 */
export interface CoarseBands {
  /** Highest fine value that still reads as `low` (inclusive). */
  low: number;
  /** Highest fine value that still reads as `medium` (inclusive). */
  medium: number;
}

const DEFAULT_COARSE_BANDS: CoarseBands = { low: DEFAULT_LOW_MAX, medium: DEFAULT_MEDIUM_MAX };

/**
 * Whether these cut points leave all three bands reachable — `is_valid` in
 * skan.rs. A band no fine value can land in is almost always a typo, and
 * honouring it would collapse a whole tier of installs into the wrong bucket.
 */
function bandsAreValid(bands: CoarseBands): boolean {
  return bands.low < bands.medium && bands.medium < MAX_FINE_VALUE;
}

/**
 * Read a `skan.coarseBands` block: `{ "low": 20, "medium": 42 }`, each an
 * INCLUSIVE band ceiling. Returns null for a missing, malformed, or degenerate
 * block so the caller falls back to the defaults — this is server data, and a
 * bad knob must not be able to disable derivation. Port of
 * `parse_coarse_bands` (skan.rs).
 */
function parseCoarseBands(value: unknown): CoarseBands | null {
  if (!isPlainObject(value)) return null;
  // `asNumber` also accepts numeric strings, matching Rust's `as_f64` and the
  // rest of this module's tolerance for a stringly-typed config.
  const read = (key: string): number | null => {
    const n = asNumber(value[key]);
    if (n === null || !Number.isInteger(n) || n < 0 || n > MAX_FINE_VALUE) return null;
    return n;
  };
  const low = read('low');
  const medium = read('medium');
  if (low === null || medium === null) return null;
  const bands = { low, medium };
  return bandsAreValid(bands) ? bands : null;
}

/**
 * The coarse conversion value a fine value implies under the default bands —
 * `coarse_for_fine` in skan.rs.
 *
 * This is the derivation every preset relies on: preset rules carry no explicit
 * `coarseValue`, so without it every preset-configured install posts a null
 * coarse value and SKAN 4.0's coarse-only privacy tiers receive nothing.
 */
export function coarseForFine(fine: number): string {
  return coarseForFineWithBands(fine, DEFAULT_COARSE_BANDS);
}

/**
 * {@link coarseForFine} against server-tuned cut points. Bands are inclusive
 * upper bounds, and `fine` is clamped to Apple's 6-bit ceiling first so an
 * out-of-range rule value cannot escape the `high` band.
 * Port of `coarse_for_fine_with_bands` (skan.rs).
 */
export function coarseForFineWithBands(fine: number, bands: CoarseBands): string {
  const value = Math.min(Math.max(Math.trunc(fine), 0), MAX_FINE_VALUE);
  if (value <= bands.low) return 'low';
  if (value <= bands.medium) return 'medium';
  return 'high';
}

export interface SkanRule {
  eventName: string;
  conditions?: Record<string, unknown> | null;
  conversionValue: number;
  coarseValue?: string | null;
  lockWindow?: boolean;
  priority?: number;
}

export interface SkanDecision {
  fineValue: number;
  /**
   * The SKAN 4.0 coarse value to post alongside `fineValue`. Always populated:
   * a rule's explicit `coarseValue` when it has one, otherwise derived from
   * `fineValue` by {@link coarseForFineWithBands}. The `| null` remains because
   * the WASM boundary and the wrappers' types declare it, but no matched rule
   * produces null any more.
   */
  coarseValue: string | null;
  lockWindow: boolean;
}

type PresetName = 'subscriptions' | 'engagement' | 'iap';

/**
 * Port of `normalize_preset` (skan.rs). `ecommerce` aliases `iap`, and the
 * server's SINGULAR `subscription` aliases `subscriptions` — the server
 * normalizes a submitted `subscriptions` down to `subscription`, so that is the
 * spelling a device actually receives, and it used to fall through to `null`
 * with zero rules installed. `lead_gen` is deliberately absent: no rule table
 * exists for it yet, and inventing one would post wrong conversion values.
 */
function normalizePreset(preset: string): PresetName | null {
  switch (preset.toLowerCase()) {
    case 'subscriptions':
    case 'subscription':
      return 'subscriptions';
    case 'engagement':
      return 'engagement';
    case 'iap':
    case 'ecommerce':
      return 'iap';
    default:
      return null;
  }
}

/**
 * Ceiling on distinct rejected preset names reported to the wire per process —
 * skan.rs `MAX_REPORTED_REJECTIONS`.
 */
const MAX_REPORTED_REJECTIONS = 8;

/**
 * A SKAN configuration failure that has to reach the wire to be seen at all —
 * port of `SkanDiagnostic` (skan.rs). Every one leaves the device with zero
 * conversion-value rules, which is wire-identical to an app that never
 * configured SKAN, so nothing else distinguishes them. `LayersCore` drains the
 * queue and emits one event per entry.
 */
export type SkanDiagnostic =
  /** No rule table for this preset name → `$skan_preset_rejected`. */
  | { kind: 'presetRejected'; preset: string }
  /** The block was a JSON string that decoded to an object → `$skan_config_string_decoded`. */
  | { kind: 'configStringDecoded' }
  /** The block was a non-object no single parse fixes → `$skan_config_rejected`. */
  | { kind: 'configRejected'; jsonType: string };

/**
 * The JSON type name of a value, as carried by `$skan_config_type`. Port of
 * `json_type_name` (skan.rs) — the spellings MUST match so the two engines
 * group together in a server-side query. `undefined` has no JSON spelling and
 * maps to `null`, JSON's own way of saying absent.
 */
function jsonTypeName(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  switch (typeof value) {
    case 'boolean':
      return 'bool';
    case 'number':
      return 'number';
    case 'string':
      return 'string';
    default:
      return 'object';
  }
}

function rule(
  eventName: string,
  conditions: Record<string, unknown> | null,
  conversionValue: number,
  priority: number
): SkanRule {
  return { eventName, conditions, conversionValue, coarseValue: null, lockWindow: false, priority };
}

/**
 * The canonical preset tables — skan.rs:436-500. Every wrapper is pinned to
 * these values by a parity test; the engagement `session_count` thresholds use
 * `>=` on purpose (older wrappers drifted to `>`).
 */
function presetRules(preset: PresetName): SkanRule[] {
  switch (preset) {
    case 'subscriptions':
      return [
        rule('app_open', null, 1, 1),
        rule('screen_view', { screen_name: 'onboarding_complete' }, 8, 3),
        // Second trigger for the same tier: the standard `onboarding_complete`
        // event. Same value and priority, so the decoder schema is unchanged.
        rule('onboarding_complete', null, 8, 3),
        rule('trial_start', null, 20, 5),
        rule('purchase_success', { revenue: { '<': 5 } }, 35, 7),
        rule('subscription_start', null, 50, 10),
        rule('subscription_renew', null, 63, 15)
      ];
    case 'engagement':
      return [
        rule('app_open', null, 1, 1),
        rule('content_open', null, 5, 2),
        rule('search', null, 12, 4),
        rule('bookmark_add', null, 25, 6),
        rule('app_open', { session_count: { '>=': 3 } }, 40, 8),
        rule('app_open', { session_count: { '>=': 10 } }, 63, 12)
      ];
    case 'iap':
      return [
        rule('app_open', null, 1, 1),
        rule('paywall_show', null, 8, 2),
        rule('purchase_attempt', null, 15, 3),
        rule('purchase_success', { revenue: { '<': 1 } }, 25, 5),
        rule('purchase_success', { revenue: { '>=': 1, '<': 10 } }, 40, 7),
        rule('purchase_success', { revenue: { '>=': 10 } }, 63, 10)
      ];
  }
}

// ── Matching ─────────────────────────────────────────────────────────────

/**
 * Coerce to a number, parsing numeric STRINGS too — skan.rs:397-403. This is
 * deliberately looser than the feature-flag engine's `as_f64`, because
 * wrappers routinely stringify revenue before it reaches the SDK.
 */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Structural equality, standing in for `serde_json::Value: PartialEq`.
 *
 * `===` is not enough: an array-valued condition (`{ tags: ["a","b"] }`)
 * reaches here — `condition_matches` only diverts OBJECTS to the operator-map
 * branch — and two structurally identical arrays are different references, so
 * a rule Rust matches would never fire.
 */
function structurallyEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => structurallyEqual(item, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const aKeys = Object.keys(ao);
  return (
    aKeys.length === Object.keys(bo).length &&
    aKeys.every((k) => k in bo && structurallyEqual(ao[k], bo[k]))
  );
}

/** Port of `values_equal` (skan.rs:388-394). A missing property never equals. */
function valuesEqual(actual: unknown, expected: unknown): boolean {
  if (actual === undefined) return false;
  const a = asNumber(actual);
  const b = asNumber(expected);
  if (a !== null && b !== null) return a === b;
  return structurallyEqual(actual, expected);
}

/** Port of `evaluate_operator` (skan.rs:362-384). Unknown operators never match. */
function evaluateOperator(actual: unknown, op: string, expected: unknown): boolean {
  switch (op) {
    case '>':
    case '>=':
    case '<':
    case '<=': {
      const a = asNumber(actual);
      const b = asNumber(expected);
      if (a === null || b === null) return false;
      if (op === '>') return a > b;
      if (op === '>=') return a >= b;
      if (op === '<') return a < b;
      return a <= b;
    }
    case '==':
    case '=':
      return valuesEqual(actual, expected);
    case '!=':
      return !valuesEqual(actual, expected);
    default:
      return false;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Port of `condition_matches` (skan.rs:351-360). */
function conditionMatches(actual: unknown, expected: unknown): boolean {
  if (isPlainObject(expected)) {
    return Object.entries(expected).every(([op, val]) => evaluateOperator(actual, op, val));
  }
  return valuesEqual(actual, expected);
}

/** Port of `rule_matches` (skan.rs:336-349). */
function ruleMatches(r: SkanRule, eventName: string, properties: Record<string, unknown>): boolean {
  if (r.eventName !== eventName) return false;
  const { conditions } = r;
  if (conditions === null || conditions === undefined) return true;
  // A non-object `conditions` is malformed — no extra constraints rather than
  // crashing or never matching (skan.rs:345-347).
  if (!isPlainObject(conditions)) return true;
  return Object.entries(conditions).every(([key, expected]) =>
    conditionMatches(properties[key], expected)
  );
}

/**
 * Deserialize one raw rule. Rust parses into `SkanRule` where `conversion_value`
 * is a `u8` and `event_name` is required, so anything outside those bounds
 * fails the whole `set_rules` call rather than being silently skipped.
 */
function parseRule(raw: unknown): SkanRule {
  if (!isPlainObject(raw)) {
    throw new LayersError('INVALID_ARGUMENT', 'invalid SKAN rules JSON: rule must be an object');
  }
  const { eventName, conversionValue } = raw;
  if (typeof eventName !== 'string') {
    throw new LayersError('INVALID_ARGUMENT', 'invalid SKAN rules JSON: missing eventName');
  }
  if (
    typeof conversionValue !== 'number' ||
    !Number.isInteger(conversionValue) ||
    conversionValue < 0 ||
    conversionValue > 255
  ) {
    throw new LayersError(
      'INVALID_ARGUMENT',
      'invalid SKAN rules JSON: conversionValue must be an integer in 0..=255'
    );
  }
  // The optional fields are typed in Rust too (`Option<String>`, `bool`,
  // `i32`), so a wrong type there fails the whole `set_rules` rather than
  // being coerced. Coercing instead would let a rule the Rust core rejected
  // outright quietly take effect on React Native.
  const { coarseValue, lockWindow, priority } = raw;
  if (coarseValue !== undefined && coarseValue !== null && typeof coarseValue !== 'string') {
    throw new LayersError(
      'INVALID_ARGUMENT',
      'invalid SKAN rules JSON: coarseValue must be a string'
    );
  }
  if (lockWindow !== undefined && typeof lockWindow !== 'boolean') {
    throw new LayersError('INVALID_ARGUMENT', 'invalid SKAN rules JSON: lockWindow must be a bool');
  }
  if (priority !== undefined && (typeof priority !== 'number' || !Number.isInteger(priority))) {
    throw new LayersError(
      'INVALID_ARGUMENT',
      'invalid SKAN rules JSON: priority must be an integer'
    );
  }
  return {
    eventName,
    // `conditions` is an untyped `Option<serde_json::Value>` in Rust, so ANY
    // JSON value deserializes; a non-object one is then treated as "no extra
    // constraints" at match time (skan.rs:345-347), which is what null does
    // here.
    conditions: isPlainObject(raw.conditions) ? raw.conditions : null,
    conversionValue,
    coarseValue: typeof coarseValue === 'string' ? coarseValue : null,
    lockWindow: lockWindow === true,
    priority: typeof priority === 'number' ? priority : 0
  };
}

/**
 * Highest priority first. `Array.prototype.sort` is stable (ES2019), which is
 * what `sort_by_key(Reverse(priority))` relies on: equal priorities keep their
 * declared order.
 */
function sortRules(rules: SkanRule[]): SkanRule[] {
  return rules.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

function clampRules(rules: SkanRule[]): SkanRule[] {
  for (const r of rules) {
    if (r.conversionValue > MAX_FINE_VALUE) r.conversionValue = MAX_FINE_VALUE;
  }
  return rules;
}

// ── Engine ───────────────────────────────────────────────────────────────

/** Port of `SkanManager` (skan.rs:77-318). */
export class SkanEngine {
  private rules: SkanRule[] = [];
  private currentValueInternal = 0;
  private currentPresetInternal: string | null = null;
  private evaluationCountInternal = 0;
  private enabled = false;
  /**
   * Cut points used to DERIVE a coarse value for a rule that carries none.
   * Refreshed from `skan.coarseBands` on every `configure`, so removing the
   * knob server-side reverts to the defaults. Port of `coarse_bands` (skan.rs).
   */
  private coarseBands: CoarseBands = DEFAULT_COARSE_BANDS;
  private lastRejectedPresetInternal: string | null = null;
  private rejectedPresetCountInternal = 0;
  /**
   * Diagnostics not yet surfaced to the wire. Every producer dedups before
   * pushing (the three fields below), so this cannot grow without bound even if
   * nothing ever drains it — which is what lets `LayersCore` drain it with a
   * plain `while` loop. Port of `pending_diagnostics` (skan.rs).
   */
  private readonly pendingDiagnostics: SkanDiagnostic[] = [];
  private readonly reportedRejections = new Set<string>();
  /** One `configStringDecoded` per process — the server repeats the block. */
  private configStringDecodedReported = false;
  /** Deduped per JSON type; bounded by the five non-object JSON types. */
  private readonly reportedConfigRejections = new Set<string>();

  /** Port of `set_preset` (skan.rs). Unknown presets are recorded, then throw. */
  setPreset(preset: string): void {
    const key = normalizePreset(preset);
    if (key === null) {
      this.recordRejectedPreset(preset);
      throw new LayersError('INVALID_ARGUMENT', `unknown SKAN preset: ${preset}`);
    }
    this.rules = sortRules(presetRules(key));
    this.currentPresetInternal = key;
    this.enabled = true;
  }

  /**
   * Port of `record_rejected_preset` (skan.rs). Zero installed rules is the same
   * observable state as "never configured SKAN", so a rejection that only throws
   * is invisible once the caller catches it. Records a console warning, the
   * accessors below, and a one-shot flag `LayersCore` drains into a
   * `$skan_preset_rejected` event — the only one of the three that leaves the
   * device, since with no rules no SKAN event can fire to carry a property.
   */
  private recordRejectedPreset(preset: string): void {
    // The log and the counter fire on EVERY rejection, before any cap or dedup
    // — same order as skan.rs. The cap bounds the event QUEUE, not the local
    // signal; gating the warn behind it would silence the ninth distinct bad
    // name entirely, and React Native runs this engine in production.
    console.warn(`[Layers] unknown SKAN preset — no conversion-value rules installed: ${preset}`);
    this.rejectedPresetCountInternal += 1;
    this.lastRejectedPresetInternal = preset;
    // An empty name carries nothing a server-side query could group on, so it
    // stays a local signal and never spends a report slot (skan.rs).
    if (preset.trim() === '') return;
    if (
      this.reportedRejections.size < MAX_REPORTED_REJECTIONS &&
      !this.reportedRejections.has(preset)
    ) {
      this.reportedRejections.add(preset);
      this.pendingDiagnostics.push({ kind: 'presetRejected', preset });
    }
  }

  /**
   * Port of `record_config_string_decoded` (skan.rs). One per process: the
   * server repeats the same double-encoded block on every ~300s refresh and the
   * signal is "this install saw it at all".
   */
  private recordConfigStringDecoded(): void {
    console.warn(
      '[Layers] SKAN config block arrived as a JSON string — decoded one level and configured from it'
    );
    if (this.configStringDecodedReported) return;
    this.configStringDecodedReported = true;
    this.pendingDiagnostics.push({ kind: 'configStringDecoded' });
  }

  /**
   * Port of `record_config_rejected` (skan.rs). Deduped per JSON type per
   * process. The warn fires on EVERY rejection, before the dedup — same order as
   * `recordRejectedPreset`, and React Native runs this engine in production.
   */
  private recordConfigRejected(jsonType: string): void {
    console.warn(
      `[Layers] SKAN config block is not an object (${jsonType}) — SKAN disabled with no conversion-value rules`
    );
    if (this.reportedConfigRejections.has(jsonType)) return;
    this.reportedConfigRejections.add(jsonType);
    this.pendingDiagnostics.push({ kind: 'configRejected', jsonType });
  }

  /** The most recent preset name this process could not install. */
  lastRejectedPreset(): string | null {
    return this.lastRejectedPresetInternal;
  }

  /** How many preset applications this process rejected. */
  rejectedPresetCount(): number {
    return this.rejectedPresetCountInternal;
  }

  /**
   * Drain the next diagnostic awaiting wire reporting. Every producer dedups
   * before pushing, so a bad config the server repeats on every refresh
   * produces one event rather than one per refresh — and the queue cannot grow
   * without bound, which is what lets the drain loop always terminate.
   * Port of `take_pending_diagnostic` (skan.rs).
   */
  takePendingDiagnostic(): SkanDiagnostic | null {
    return this.pendingDiagnostics.shift() ?? null;
  }

  /** Port of `set_rules` (skan.rs:111-121). */
  setRules(rulesJson: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rulesJson);
    } catch (e) {
      throw new LayersError('INVALID_ARGUMENT', `invalid SKAN rules JSON: ${String(e)}`);
    }
    if (!Array.isArray(parsed)) {
      throw new LayersError('INVALID_ARGUMENT', 'invalid SKAN rules JSON: expected an array');
    }
    this.rules = sortRules(clampRules(parsed.map(parseRule)));
    this.currentPresetInternal = 'custom';
    this.enabled = true;
  }

  /**
   * Port of `configure` (skan.rs) — apply a remote-config `skan` block.
   * Precedence: `enabled:false` disables; a `preset` beats `customRules`; a
   * block with neither bare-enables only on an explicit `enabled: true`. The
   * install-scoped monotonic floor is preserved through every branch.
   *
   * A non-object block is **decoded or loud**, never silent:
   *
   * - A **string** gets exactly one `JSON.parse`. If that yields an object,
   *   SKAN is configured from it and a `configStringDecoded` diagnostic is
   *   queued. The server stored `sdk_apps.skan_config` double-encoded for 25
   *   apps from 2026-03-19 and served `"skan": "{\"enabled\":true,…}"`; those
   *   apps ran with zero conversion-value rules for five months because this
   *   returned after disabling. Exactly one level is decoded — looping would
   *   make the parse depth a function of server data.
   * - Anything else keeps the disable semantics and queues a `configRejected`
   *   carrying the JSON type name.
   */
  configure(skan: unknown): void {
    if (isPlainObject(skan)) {
      this.configureObject(skan);
      return;
    }

    if (typeof skan === 'string') {
      let decoded: unknown;
      try {
        decoded = JSON.parse(skan);
      } catch {
        // Deliberately not silent: an unparseable string falls through to the
        // `recordConfigRejected` below, which warns and emits
        // `$skan_config_rejected`. Swallowing here is what makes the ONE parse
        // attempt total rather than throwing out of a config refresh.
        decoded = undefined;
      }
      if (isPlainObject(decoded)) {
        this.recordConfigStringDecoded();
        // `decoded` is a plain object, so this cannot re-enter the branch
        // above: the decode is one level deep by construction.
        this.configureObject(decoded);
        return;
      }
    }

    // null, a bool, a number, an array, or a string no single parse turns into
    // an object. Disabling stays the behaviour — arming SKAN off a shape we
    // cannot read would be worse — but it is now audible on the device and on
    // the wire. Note `LayersCore.skanConfigureFromRemoteConfig` short-circuits
    // an explicitly-null block before it reaches here: `"skan": null` is the
    // server's ordinary "this app has no SKAN config".
    this.recordConfigRejected(jsonTypeName(skan));
    this.disable();
  }

  /**
   * The body of {@link configure} once the block is known to be a plain object.
   * Split out so the string-decode path above has exactly one non-recursive
   * place to hand its decoded object. Port of `configure_object` (skan.rs).
   */
  private configureObject(skan: Record<string, unknown>): void {
    // Refresh the coarse cut points FIRST, before any of the early returns
    // below — a `{ preset, coarseBands }` block sets both, and the preset branch
    // returns without reaching the bottom of this method. An absent or
    // malformed knob resets to the defaults, so the server can retract a tuning
    // by dropping the key (skan.rs `configure_object`).
    const rawBands = skan.coarseBands;
    const parsedBands =
      rawBands === null || rawBands === undefined ? null : parseCoarseBands(rawBands);
    if (rawBands !== null && rawBands !== undefined && parsedBands === null) {
      console.warn(
        '[Layers] SKAN coarseBands is malformed or leaves a band unreachable — falling back to the default 0-20 / 21-42 / 43-63 split'
      );
    }
    this.coarseBands = parsedBands ?? DEFAULT_COARSE_BANDS;

    // `enabled` is normally a bool; coerce defensively so a stringly-typed
    // "false"/"0"/"" or the number 0 reads as disabled (skan.rs:142-150).
    let enabledFlag: boolean | undefined;
    if ('enabled' in skan) {
      const raw = skan.enabled;
      if (typeof raw === 'boolean') enabledFlag = raw;
      else if (raw === null) enabledFlag = false;
      else if (typeof raw === 'string')
        enabledFlag = !['false', '0', ''].includes(raw.toLowerCase());
      else if (typeof raw === 'number') enabledFlag = raw !== 0;
      else enabledFlag = true;
    }

    if (enabledFlag === false) {
      this.disable();
      return;
    }

    if (typeof skan.preset === 'string') {
      try {
        this.setPreset(skan.preset);
      } catch (e) {
        // Unknown preset: clear the stale tiers so they stop firing, preserve
        // the floor, do NOT force-enable, and propagate (skan.rs:159-172).
        this.rules = [];
        this.currentPresetInternal = null;
        throw e;
      }
      return;
    }

    // Both `customRules` (config convention) and `rules` are accepted; a null
    // value means "no custom rules" and falls through (skan.rs:177-181).
    const rawRules = skan.customRules ?? skan.rules;
    if (rawRules !== null && rawRules !== undefined) {
      if (Array.isArray(rawRules)) {
        this.setRules(JSON.stringify(rawRules));
        return;
      }
      this.rules = [];
      this.currentPresetInternal = null;
      throw new LayersError('INVALID_ARGUMENT', 'SKAN customRules must be a JSON array');
    }

    // Bare-enable for basic install attribution ONLY on an explicit
    // `{ "enabled": true }`. `{}` disables, matching an omitted block.
    if (enabledFlag === true) {
      this.enabled = true;
      this.rules = [];
      this.currentPresetInternal = null;
    } else {
      this.disable();
    }
  }

  /**
   * Port of `process_event` (skan.rs:228-257). Returns the OS-level update to
   * apply, or null when nothing matched or the value would not increase. Does
   * NOT advance the floor — {@link recordConversionResult} does, on success.
   */
  processEvent(eventName: string, properties: Record<string, unknown> = {}): SkanDecision | null {
    this.evaluationCountInternal += 1;
    if (!this.enabled) return null;

    const matched = this.rules.find((r) => ruleMatches(r, eventName, properties));
    if (!matched) return null;
    if (matched.conversionValue <= this.currentValueInternal) return null;

    // A rule's explicit `coarseValue` always wins. When it carries none — which
    // is EVERY preset rule — derive one from the fine value it just matched, so
    // SKAN 4.0's coarse-only privacy tiers get a signal instead of null. An
    // explicit-but-blank string counts as absent: it would otherwise reach
    // StoreKit as an unmappable value.
    const fineValue = Math.min(matched.conversionValue, MAX_FINE_VALUE);
    const explicit = typeof matched.coarseValue === 'string' ? matched.coarseValue.trim() : '';

    return {
      fineValue,
      coarseValue: explicit !== '' ? explicit : coarseForFineWithBands(fineValue, this.coarseBands),
      lockWindow: matched.lockWindow === true
    };
  }

  /**
   * Port of `record_conversion_result` (skan.rs:264-276). Returns true when
   * the floor actually advanced, so the caller knows to persist it.
   */
  recordConversionResult(value: number, success: boolean): boolean {
    if (!success) return false;
    const v = Math.min(Math.max(Math.trunc(value), 0), MAX_FINE_VALUE);
    if (v > this.currentValueInternal) {
      this.currentValueInternal = v;
      return true;
    }
    return false;
  }

  currentValue(): number {
    return this.currentValueInternal;
  }

  currentPreset(): string | null {
    return this.currentPresetInternal;
  }

  ruleCount(): number {
    return this.rules.length;
  }

  evaluationCount(): number {
    return this.evaluationCountInternal;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Port of `disable` (skan.rs:303-308). PRESERVES the monotonic floor. */
  disable(): void {
    this.enabled = false;
    this.rules = [];
    this.currentPresetInternal = null;
  }

  /** Port of `seed_value` (skan.rs:312-317). Only ever raises the value. */
  seedValue(value: number): void {
    const v = Math.min(Math.max(Math.trunc(value), 0), MAX_FINE_VALUE);
    if (v > this.currentValueInternal) this.currentValueInternal = v;
  }
}
